import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { imgUrl, logoUrl, assetUrl, API, shootCover, runJob } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe, ScanFace, Images, Copy, Download, Film,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import MasterSection from "./MasterSection";
import CoverMemberSection from "./CoverMemberSection";
import EditSection from "./EditSection";
import SpotlightSection from "./SpotlightSection";
import FiveQuestionsSection from "./FiveQuestionsSection";
import NoticeboardSection from "./NoticeboardSection";
import MembersDeskSection from "./MembersDeskSection";
import AdvertSection from "./AdvertSection";
import SocialSection from "./SocialSection";
import BusinessImageStudio from "./BusinessImageStudio";
import PhotoFlow from "./PhotoFlow";
import MemberFormPreview from "./MemberFormPreview";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";
import { AD_SLOT_META, CATEGORIES, SECTIONS, TONES, LENGTHS, MCATS, BUSINESS_CATS, Header, Stat, InboxGroup, InboxRow, DIn, DArea, Badge, Panel, Drawer, Field, Area, Select, ComboField } from "./shared";

const STUDIO_PANELS = [
  { id: "section-photoflow", label: "Photo Flow", note: "Photos in → re-shoot → use everywhere" },
  { id: "section-master", label: "Master", note: "Source of truth" },
  { id: "section-cover-member", label: "Cover Member", note: "Portrait + profile" },
  { id: "section-business-images", label: "Business Images", note: "On-brand product & premises shots" },
  { id: "section-edit", label: "The Edit", note: "Editorial articles" },
  { id: "section-spotlight", label: "Spotlight", note: "Brand feature" },
  { id: "section-fiveq", label: "5 Questions", note: "Interview" },
  { id: "section-noticeboard", label: "Noticeboard", note: "Offers & posters" },
  { id: "section-desk", label: "Members' Desk", note: "Blog & long reads" },
  { id: "section-advert", label: "Advert", note: "Premium placements" },
  { id: "section-social", label: "Social", note: "Instagram · TikTok" },
];

const StudioNav = ({ readiness }) => {
  const [active, setActive] = useState("section-photoflow");
  useEffect(() => {
    const opts = { rootMargin: "-30% 0px -60% 0px", threshold: 0 };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
    }, opts);
    STUDIO_PANELS.forEach((p) => {
      const el = document.getElementById(p.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);
  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const sectionDone = (label) => {
    if (!readiness?.items) return null;
    const items = readiness.items.filter((it) => it.section === label);
    if (!items.length) return null;
    const done = items.filter((it) => it.done).length;
    return { done, total: items.length, complete: done === items.length };
  };
  return (
    <nav className="sticky top-[76px] z-20 -mx-6 sm:-mx-10 mb-6 bg-cream border-y border-ink/15" data-testid="studio-nav">
      <div className="flex overflow-x-auto no-scrollbar">
        {STUDIO_PANELS.map((p) => {
          const isActive = active === p.id;
          const s = sectionDone(p.label);
          return (
            <button
              key={p.id}
              onClick={() => jump(p.id)}
              className={`relative shrink-0 px-4 sm:px-5 py-3 flex items-center gap-2 border-r border-ink/10 transition-colors ${isActive ? "bg-ink text-cream" : "text-ink hover:bg-ink/5"}`}
              data-testid={`studio-nav-${p.id}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? "bg-gloss-gold" : s?.complete ? "bg-green-600" : "bg-ink/25"}`} />
              <span className="font-grotesk text-sm font-medium whitespace-nowrap">{p.label}</span>
              {s && <span className={`font-mono text-[10px] ${isActive ? "text-cream/60" : "text-ink/40"}`}>{s.done}/{s.total}</span>}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

const HubSection = ({ id, label, note, single, children }) => (
  <section id={id} data-testid={id} className="mb-14">
    <div className="bg-ink text-cream px-5 py-3 mb-7 flex flex-wrap items-baseline gap-x-4 gap-y-1 grain relative">
      <span className="overline text-gloss-gold relative z-10">{label}</span>
      {note && <span className="font-mono text-[11px] text-cream/50 relative z-10">{note}</span>}
    </div>
    <div className={single ? "space-y-10" : "grid grid-cols-1 xl:grid-cols-2 gap-x-12 gap-y-10 items-start"}>{children}</div>
  </section>
);

export default function MemberEditor({ member, onBack, onChange }) {
  const [m, setM] = useState(member);
  const [busy, setBusy] = useState("");
  const [edCoverStyle, setEdCoverStyle] = useState("Vogue/GQ editorial fashion cover — clean elegant studio lighting, rich natural colour, high-end fashion styling, confident cover-star presence. Real fashion photography: NO smoke, NO haze, NO fog, NO coloured gel lighting, NO theatrical effects.");
  const [cmCoverStyle, setCmCoverStyle] = useState("Fresh, edgy cover-star portrait — bold colour backdrop, modern lighting with attitude, relaxed confident pose, full of character. Cool British indie-magazine energy, never corporate, never stiff");
  const [blogTopic, setBlogTopic] = useState("");
  const [notice, setNotice] = useState({ title: "", body: "", type: "offer" });
  const [lastNoticeId, setLastNoticeId] = useState(null);
  const [genStep, setGenStep] = useState("");
  const [adSlot, setAdSlot] = useState((member.advert_run || {}).slot || "home_band_a");
  const [adIssue, setAdIssue] = useState((member.advert_run || {}).issue || editionStartIso(cycleIndexFor() + 1));
  const [adSpaces, setAdSpaces] = useState([]);
  useEffect(() => {
    client.get("/admin/ad-spaces", { params: { issue: adIssue } }).then((r) => setAdSpaces(r.data.spaces || [])).catch(() => {});
  }, [adIssue, member.advert_run]); // eslint-disable-line react-hooks/exhaustive-deps
  const [buildUrl, setBuildUrl] = useState(member.website || "");
  useEffect(() => { setBuildUrl(member.website || ""); }, [member]);
  useEffect(() => { setM(member); }, [member]);

  const [articles, setArticles] = useState({});
  const [inUse, setInUse] = useState({});
  const loadArticles = async () => {
    try {
      const r = await client.get(`/admin/members/${m.id}/articles-map`);
      setArticles(r.data.articles || {});
      setInUse(r.data.in_use || {});
    } catch { /* ignore */ }
  };
  useEffect(() => { loadArticles(); }, [m.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const articleChanged = (kind, art) => {
    setArticles((prev) => ({ ...prev, [kind]: art }));
    loadArticles();
  };


  const reallocatePlan = async () => {
    setBusy("alloc");
    try {
      const r = await client.post(`/admin/members/${m.id}/allocate-plan`, { force: true });
      setM((prev) => ({ ...prev, year_plan: r.data.year_plan, advert_run: r.data.advert_run }));
      toast.success("Fair 12-month rotation allocated — change anything you like.");
      onChange();
    } catch { toast.error("Could not allocate the year plan."); }
    setBusy("");
  };

  const advertRun = async (action) => {
    setBusy("adrun");
    try {
      const payload = { action, slot: adSlot };
      if (action === "schedule") payload.issue = adIssue;
      const r = await client.post(`/admin/members/${m.id}/advert/run`, payload);
      setM((prev) => ({ ...prev, advert_run: r.data.advert_run }));
      if (r.data.warning) toast.warning(r.data.warning);
      else toast.success(action === "publish_now" ? "Advert is live in the current edition." : action === "schedule" ? "Advert scheduled." : "Advert taken off.");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update the advert run."); }
    setBusy("");
  };

  const [readiness, setReadiness] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const loadReadiness = async () => { try { const r = await client.get(`/admin/members/${m.id}/readiness`); setReadiness(r.data); } catch {} };
  const [content, setContent] = useState(null);
  const loadContent = async () => { try { const r = await client.get(`/admin/members/${m.id}/content`); setContent(r.data); } catch {} };
  useEffect(() => { if (m.id) { loadReadiness(); loadContent(); } /* eslint-disable-next-line */ }, [m.id]);
  useEffect(() => { if (m.id && busy === "") { loadReadiness(); loadContent(); } /* eslint-disable-next-line */ }, [busy]);

  const setArticleStatus = async (id, status) => {
    try { await client.patch(`/admin/articles/${id}`, { status }); toast.success(status === "published" ? "Published — live now." : "Unpublished."); loadContent(); }
    catch { toast.error("Could not update."); }
  };
  const setNoticeStatus = async (id, status) => {
    try { await client.patch(`/admin/notices/${id}`, { status }); toast.success(status === "published" ? "Poster published." : "Poster unpublished."); loadContent(); }
    catch { toast.error("Could not update."); }
  };
  const [imgBusy, setImgBusy] = useState("");
  const regenArticleImage = async (articleId, type) => {
    setImgBusy(type);
    try { await runJob(`/admin/articles/${articleId}/regenerate-image`); toast.success("Fresh photo generated — the copy is unchanged."); loadContent(); }
    catch { toast.error("Could not regenerate the photo."); }
    setImgBusy("");
  };
  const uploadArticleImage = async (articleId, type, files) => {
    if (!files?.[0]) return; setImgBusy(type);
    const fd = new FormData(); fd.append("file", files[0]);
    try { await client.post(`/admin/articles/${articleId}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo replaced — the copy is unchanged."); loadContent(); }
    catch { toast.error("Upload failed."); }
    setImgBusy("");
  };
  const [imgOptions, setImgOptions] = useState({});
  const [optBusy, setOptBusy] = useState("");
  const getImageOptions = async (articleId, type) => {
    setOptBusy(type);
    try { const r = await runJob(`/admin/articles/${articleId}/image-options?count=3`); setImgOptions((o) => ({ ...o, [articleId]: r.data.options })); }
    catch { toast.error("Could not generate options."); }
    setOptBusy("");
  };
  const pickImageOption = async (articleId, image) => {
    try { await client.post(`/admin/articles/${articleId}/select-image`, { image }); setImgOptions((o) => { const n = { ...o }; delete n[articleId]; return n; }); toast.success("Photo set — the copy is unchanged."); loadContent(); }
    catch { toast.error("Could not set the photo."); }
  };
  const deleteArticle = async (articleId, label) => {
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    try { await client.delete(`/admin/articles/${articleId}`); toast.success(`${label} deleted.`); loadContent(); }
    catch { toast.error("Could not delete."); }
  };
  const deleteNotice = async (noticeId) => {
    if (!window.confirm("Delete this Noticeboard poster? This cannot be undone.")) return;
    try { await client.delete(`/admin/notices/${noticeId}`); toast.success("Poster deleted."); loadContent(); }
    catch { toast.error("Could not delete."); }
  };
  const [schedBusy, setSchedBusy] = useState("");
  const scheduleArticle = async (articleId, type, publishAt) => {
    setSchedBusy(type);
    try {
      await client.post(`/admin/articles/${articleId}/schedule`, { publish_at: publishAt || null });
      toast.success(publishAt ? "Scheduled — it drips out on the chosen date." : "Schedule cleared — back to draft.");
      loadContent();
    } catch { toast.error("Could not schedule."); }
    setSchedBusy("");
  };
  const regenNoticePoster = async (noticeId) => {
    setImgBusy(`notice-${noticeId}`);
    try { await client.post(`/admin/notices/${noticeId}/generate-poster`); toast.success("Fresh poster generated."); loadContent(); }
    catch { toast.error("Could not regenerate the poster."); }
    setImgBusy("");
  };
  const uploadNoticePoster = async (noticeId, files) => {
    if (!files?.[0]) return; setImgBusy(`notice-${noticeId}`);
    const fd = new FormData(); fd.append("file", files[0]);
    try { await client.post(`/admin/notices/${noticeId}/poster`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Poster image replaced."); loadContent(); }
    catch { toast.error("Upload failed."); }
    setImgBusy("");
  };
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", dek: "", body: "" });
  const [editBusy, setEditBusy] = useState(false);
  const openEditPiece = async (id) => {
    if (editId === id) { setEditId(null); return; }
    try {
      const r = await client.get(`/admin/articles/${id}`);
      setEditForm({ title: r.data.title || "", dek: r.data.dek || "", body: r.data.body || "" });
      setEditId(id);
    } catch { toast.error("Could not load the copy."); }
  };
  const saveEditPiece = async () => {
    setEditBusy(true);
    try { await client.patch(`/admin/articles/${editId}`, { title: editForm.title, dek: editForm.dek, body: editForm.body }); toast.success("Copy saved."); setEditId(null); loadContent(); }
    catch { toast.error("Save failed."); }
    setEditBusy(false);
  };
  const [editNoticeId, setEditNoticeId] = useState(null);
  const [noticeForm, setNoticeForm] = useState({ title: "", body: "" });
  const openEditNotice = (n) => {
    if (editNoticeId === n.id) { setEditNoticeId(null); return; }
    setNoticeForm({ title: n.title || "", body: n.body || "" }); setEditNoticeId(n.id);
  };
  const saveEditNotice = async () => {
    setEditBusy(true);
    try { await client.patch(`/admin/notices/${editNoticeId}`, { title: noticeForm.title, body: noticeForm.body }); toast.success("Notice saved."); setEditNoticeId(null); loadContent(); }
    catch { toast.error("Save failed."); }
    setEditBusy(false);
  };
  const removeAdvert = async () => {
    if (!window.confirm("Remove this advert? This cannot be undone.")) return;
    try { await client.patch(`/admin/members/${m.id}`, { package: { ...(m.package || {}), advert: null } }); setM((p) => ({ ...p, package: { ...(p.package || {}), advert: null } })); toast.success("Advert removed."); loadContent(); }
    catch { toast.error("Could not remove advert."); }
  };
  const BACKEND = (client.defaults.baseURL || "").replace(/\/api$/, "");

  const set = (k, v) => setM({ ...m, [k]: v });
  const setFeat = (k, v) => setM({ ...m, package: { ...(m.package || {}), feature: { ...((m.package || {}).feature || {}), [k]: v } } });
  const setAd = (k, v) => setM({ ...m, package: { ...(m.package || {}), advert: { ...((m.package || {}).advert || {}), [k]: v } } });

  const saveFields = async () => {
    setBusy("save");
    try {
      await client.patch(`/admin/members/${m.id}`, {
        business_name: m.business_name, owner_name: m.owner_name, borough: m.borough, category: m.category,
        bio: m.bio, personal_bio: m.personal_bio, business_bio: m.business_bio, services: m.services, offers: m.offers,
        email: m.email, address: m.address, website: m.website, shop_url: m.shop_url || "", instagram: m.instagram, tiktok: m.tiktok, linkedin: m.linkedin,
        facebook: m.facebook || "", x_handle: m.x_handle || "",
        phone: m.phone, mobile: m.mobile, landline: m.landline, display_phone: m.display_phone !== false, use_landline: !!m.use_landline,
        ref_notes: m.ref_notes, business_category: m.business_category,
        master_instructions: m.master_instructions || "",
        key_facts: m.key_facts || "",
        opening_hours: m.opening_hours || "",
        sector: m.sector || "",
        package: m.package || {},
      });
      toast.success("Member saved."); onChange();
    } catch { toast.error("Save failed."); }
    setBusy("");
  };
  const genPackage = async () => { setBusy("package"); try { const r = await client.post(`/admin/members/${m.id}/generate-package`); setM({ ...m, package: r.data }); toast.success("AI package generated."); onChange(); } catch { toast.error("Generation failed."); } setBusy(""); };
  const magicBuild = async (mode = "media") => {
    const url = (buildUrl || m.website || "").trim();
    if (!url) { toast.error("Paste their website in the build bar first."); return; }
    setBusy(mode === "profile" ? "magic-profile" : "magic");
    toast(mode === "profile" ? "Reading their site — filling the profile & bios…" : "Reading their site — profile, their imagery and a fresh editorial cover. Give it a minute…");
    try {
      const r = await client.post(`/admin/members/${m.id}/magic-build`, { url, mode });
      setM(r.data.member);
      toast.success(`Done — profile filled${r.data.imported_photos ? `, ${r.data.imported_photos} owner photos` : ""}${r.data.imported_business_photos ? `, ${r.data.imported_business_photos} business shots` : ""}${r.data.cover_generated ? ", fresh editorial cover shot" : ""}.`);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Build failed — check the address and try again."); }
    setBusy("");
  };
  const removePhoto = async (path, field) => {
    try {
      const r = await client.post(`/admin/members/${m.id}/photos/remove`, { path, field });
      setM((prev) => ({ ...prev, [field]: r.data[field] }));
      toast.success("Photo removed.");
      onChange();
    } catch { toast.error("Couldn't remove that photo."); }
  };
  const setLikeness = async (path) => {
    try {
      const r = await client.post(`/admin/members/${m.id}/likeness`, { path });
      setM((prev) => ({ ...prev, likeness_ref: r.data.likeness_ref }));
      toast.success(path ? "Face locked — every future shoot anchors the face on this exact photo." : "Face lock removed.");
    } catch { toast.error("Could not set the face reference."); }
  };
  const createFeaturePiece = async () => {
    try {
      await client.post(`/admin/members/${m.id}/create-feature-piece`);
      toast.success("Cover Member profile created as a draft — hit Publish on it when ready.");
      loadContent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generate the Editorial Feature copy first."); }
  };
  const buildSpread = async (articleId, type) => {
    if (!window.confirm("Shoot a 3-image spread for INSIDE this article? Uses AI credits.")) return;
    setImgBusy(type);
    try {
      await client.post(`/admin/articles/${articleId}/generate-gallery`, { count: 3 });
      toast.success("The desk is shooting a 3-image spread inside this article — check the Preview in a minute.");
    } catch { toast.error("Could not start the image spread."); }
    setImgBusy("");
  };
  const masterReset = async () => {
    if (!window.confirm(`Master reset for ${m.business_name}: wipes ALL imagery (photos, business shots, covers, logo, studio gallery), the generated package and every draft piece. Profile text, schedule and published pieces stay. This cannot be undone. Continue?`)) return;
    setBusy("reset");
    try {
      const r = await client.post(`/admin/members/${m.id}/master-reset`);
      setM(r.data.member);
      toast.success(`Clean slate — media wiped${r.data.drafts_deleted ? `, ${r.data.drafts_deleted} draft piece${r.data.drafts_deleted === 1 ? "" : "s"} deleted` : ""}. Run a fresh build when ready.`);
      onChange();
    } catch { toast.error("Reset failed — try again."); }
    setBusy("");
  };
  const assignShot = async (image, target, label) => {
    if (!target) return;
    setBusy("assign");
    try {
      const r = await client.post(`/admin/members/${m.id}/shot-gallery/assign`, { image, target });
      setM(r.data.member);
      if (target.startsWith("article:")) { toast.success(`Photo assigned to ${label || "the piece"}.`); loadContent(); }
      else toast.success(target === "directory_image" ? "Assigned as Cover Member — S/ME masthead stamped on." : target === "advert_image" ? "Assigned as the advert image." : target === "insta_tile_image" ? "Assigned as the Instagram tile." : "Assigned as the Editorial cover.");
      onChange();
    } catch { toast.error("Couldn't assign that shot."); }
    setBusy("");
  };
  const removeShot = async (id) => {
    try { const r = await client.post(`/admin/members/${m.id}/shot-gallery/remove`, { id }); setM((p) => ({ ...p, shot_gallery: r.data.shot_gallery })); }
    catch { toast.error("Couldn't remove that shot."); }
  };
  const extraShot = async () => {
    if (!(m.photos || []).length) { toast.error("Upload or import their photos first."); return; }
    setBusy("extra");
    try {
      const r = await shootCover(m.id, { style: edCoverStyle, preset: "editorial", target: "gallery" });
      setM((p) => ({ ...p, shot_gallery: [...(p.shot_gallery || []), r.data.shot] }));
      toast.success("Extra shot developed — assign it from the gallery.");
      onChange();
    } catch { toast.error("Shot failed — try again."); }
    setBusy("");
  };
  const genCover = async (preset) => {
    setBusy(preset);
    const target = preset === "directory" ? "directory_image" : "cover_image";
    const style = preset === "directory" ? cmCoverStyle : edCoverStyle;
    try {
      const r = await shootCover(m.id, { style, preset, target, style_ref: styleRef?.image || null });
      setM({ ...m, [target]: r.data.image });
      toast.success(preset === "directory" ? "Cover Member shot generated (S/ME masthead · their logo)." : "Editorial cover generated.");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Cover failed — upload photos first."); }
    setBusy("");
  };
  const uploadCover = async (files, target = "cover_image") => {
    if (!files?.[0]) return; setBusy("upload");
    const fd = new FormData(); fd.append("file", files[0]);
    try { const r = await client.post(`/admin/members/${m.id}/cover-upload?target=${target}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setM({ ...m, [target]: r.data[target] }); toast.success("Photo set as cover (no AI)."); onChange(); } catch { toast.error("Upload failed."); }
    setBusy("");
  };
  const uploadPhotos = async (files) => {
    if (!files?.length) return; setBusy("photos");
    const fd = new FormData(); Array.from(files).slice(0, 5).forEach((f) => fd.append("files", f));
    try { const r = await client.post(`/admin/members/${m.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setM({ ...m, photos: r.data.photos }); toast.success("Photos uploaded — ready for AI cover."); onChange(); } catch { toast.error("Upload failed."); }
    setBusy("");
  };
  const uploadLogo = async (files) => {
    if (!files?.[0]) return; setBusy("logo");
    const fd = new FormData(); fd.append("file", files[0]);
    try { const r = await client.post(`/admin/members/${m.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setM({ ...m, logo: r.data.logo }); toast.success("Logo uploaded."); onChange(); } catch { toast.error("Upload failed."); }
    setBusy("");
  };
  const uploadRefDocs = async (files) => {
    if (!files?.length) return; setBusy("refdocs");
    const fd = new FormData(); Array.from(files).slice(0, 5).forEach((f) => fd.append("files", f));
    try { const r = await client.post(`/admin/members/${m.id}/ref-docs`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setM((prev) => ({ ...prev, ref_docs: r.data.ref_docs })); toast.success("Documents added — the AI will read them."); }
    catch { toast.error("Upload failed — PDF, DOCX or TXT only."); }
    setBusy("");
  };
  const deleteRefDoc = async (docId) => {
    try { const r = await client.delete(`/admin/members/${m.id}/ref-docs/${docId}`); setM((prev) => ({ ...prev, ref_docs: r.data.ref_docs })); toast.success("Document removed."); }
    catch { toast.error("Could not remove."); }
  };
  const assist = async (field) => {
    setBusy(field);
    const name = m.business_name || "this business";
    const cat = m.category ? `, a ${m.category}` : "";
    const where = m.borough ? ` in ${m.borough}` : "";
    const base = `${m.business_bio || ""} ${m.personal_bio || ""} ${m.bio || ""} ${m.services || ""} ${m.offers || ""}`.trim();
    const briefs = {
      bio: `Write a magnetic 2-3 sentence story/bio for ${name}${cat}${where}. Notes: ${base}`,
      personal_bio: `Write a warm, characterful 2-3 sentence bio about ${m.owner_name || "the owner"}, the person behind ${name}${cat}. Make it human and personable, first impression of a real character. Notes: ${base}`,
      business_bio: `Write a sharp 2-3 sentence bio about the business ${name}${cat}${where} — what it does and what makes it genuinely special. Notes: ${base}`,
      services: `List the key services/products of ${name}${cat} as a concise bullet list — ONE short item per line, each starting with "- ". 4-8 items, no intro sentence, no paragraphs. Base it on: ${base}`,
      offers: `Write ONE short, enticing reader offer/perk line for ${name}${cat} that S/ME readers can claim (e.g. "20% off your first booking for S/ME readers"). One line only, no preamble. Base it on: ${m.offers || base}`,
    };
    try {
      const r = await client.post("/ai/assist", { field, instruction: briefs[field], context: base, source_url: m.website || "" });
      setM((prev) => ({ ...prev, [field]: r.data.text }));
      toast.success("Written by AI — edit as you like.");
    } catch { toast.error("AI assist failed."); }
    setBusy("");
  };
  const AiBtn = ({ field, label }) => (
    <button onClick={() => assist(field)} disabled={!!busy} className="overline text-ink/70 border border-ink/30 px-3 py-1.5 flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid={`assist-${field}`}>
      {busy === field ? <RefreshCw size={13} className="animate-spin" /> : <Sparkles size={13} />} {label}
    </button>
  );
  const publish = async () => {
    if (readiness && !readiness.ready) {
      const ok = window.confirm(`This profile isn't complete yet. Missing:\n\n• ${readiness.missing_required.join("\n• ")}\n\nPublish anyway?`);
      if (!ok) return;
    }
    setBusy("publish"); try { await client.post(`/admin/members/${m.id}/publish`); toast.success("Published to magazine."); onChange(); } catch { toast.error("Publish failed."); } setBusy("");
  };
  const setVisibility = async (live) => {
    const msg = live
      ? "Put this member back on the magazine? Their profile and everything that was live before comes back exactly as it was."
      : "Take this member OFF the magazine? Their profile, live articles, advert and posters all come off public view — but EVERYTHING in the Studio is kept exactly as it is, ready to switch back on any time.";
    if (!window.confirm(msg)) return;
    setBusy("visibility");
    try {
      const r = await client.post(`/admin/members/${m.id}/visibility`, { live });
      setM(r.data.member);
      loadArticles();
      toast.success(live ? "Back on the magazine — everything restored." : "Off the magazine — everything kept safe in the Studio.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't switch."); }
    setBusy("");
  };
  const setCoverStar = async () => {
    if (!window.confirm(`Make ${m.business_name} the Cover Star of the current edition? They swap their Spotlight for a Cover Star Feature and appear at the top of the magazine.`)) return;
    setBusy("coverstar");
    try {
      const r = await client.post(`/admin/members/${m.id}/set-cover-star`);
      toast.success(`${m.business_name} is now the Cover Star.`);
      if (r.data.note) toast.info(r.data.note, { duration: 8000 });
      onChange();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not set Cover Star."); }
    setBusy("");
  };
  const [coverFilms, setCoverFilms] = useState([]);
  const loadCoverFilms = () => client.get(`/admin/videos?member_id=${m.id}`).then((r) => setCoverFilms((r.data || []).filter((v) => v.purpose === "cover"))).catch(() => {});
  useEffect(() => { if (m.id) loadCoverFilms(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [m.id]);
  const animateCoverStar = async () => {
    if (!window.confirm("Animate their Cover Member portrait into the living cover motion clip? Only Cover Stars get animated.")) return;
    setBusy("animatestar");
    try {
      await client.post(`/admin/members/${m.id}/animate-cover-star`);
      toast.success("Filming — takes a few minutes. Watch the status just below; it attaches to the cover by itself when done.");
      loadCoverFilms();
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Animate failed."); }
    setBusy("");
  };
  const attachLatestFilm = async () => {
    setBusy("attachfilm");
    try {
      const r = await client.post(`/admin/members/${m.id}/set-cover-star`);
      toast.success(r.data?.title ? "Cover refreshed — latest finished film attached." : "Cover refreshed.");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't attach the film."); }
    setBusy("");
  };
  const [photoPickFor, setPhotoPickFor] = useState(null);
  const [previewSlug, setPreviewSlug] = useState(null);
  const [styleRef, setStyleRef] = useState(null); // {image, business_name} — a studio shot used as a style reference
  const [stylePicker, setStylePicker] = useState(false);
  const [studioShots, setStudioShots] = useState(null);
  const openStylePicker = async () => {
    setStylePicker(true);
    if (!studioShots) {
      try { const r = await client.get("/admin/shots/recent?limit=80"); setStudioShots(r.data); } catch { setStudioShots([]); }
    }
  };
  const [videos, setVideos] = useState([]);
  const loadVideos = () => client.get(`/admin/videos?member_id=${m.id}`).then((r) => setVideos(r.data)).catch(() => {});
  useEffect(() => { loadVideos(); }, [m.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const animateImage = async (image, purpose) => {
    if (!window.confirm("Bring this image to life as a short cinematic clip (~5s)? Uses your video (fal.ai) credits.")) return;
    try {
      await client.post("/admin/social/animate", { image, member_id: m.id, purpose });
      toast.success(purpose === "cover"
        ? "The homepage cover is being brought to life — it will start playing on the homepage automatically in a few minutes."
        : "Motion clip is being made — check Motion clips in a few minutes.");
      loadVideos();
    } catch (e) { toast.error(e?.response?.data?.detail || "Motion isn't switched on yet — add your video key in the Social tab."); }
  };
  const writeCaption = async (id) => {
    setBusy(`cap-${id}`);
    try { await client.post(`/admin/videos/${id}/caption`); toast.success("Caption written — copy and post."); loadVideos(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Caption failed."); }
    setBusy("");
  };
  const copyCaption = (v) => {
    const c = v.caption || {};
    const text = [c.hook, c.caption, (c.hashtags || []).join(" ")].filter(Boolean).join("\n\n");
    navigator.clipboard.writeText(text).then(() => toast.success("Caption copied — paste straight into Instagram or TikTok."));
  };
  const setImageryFocus = async (v) => {
    try { await client.patch(`/admin/members/${m.id}`, { imagery_focus: v }); setM({ ...m, imagery_focus: v }); toast.success("Imagery focus saved — every new shoot follows it."); onChange(); }
    catch { toast.error("Could not save imagery focus."); }
  };
  const reshootRunning = (m.reshoot_all || {}).status === "running";
  useEffect(() => {
    if (!reshootRunning) return;
    const t = setInterval(async () => {
      try {
        const r = await client.get("/admin/members");
        const fresh = r.data.find((x) => x.id === m.id);
        if (fresh) {
          setM((prev) => ({ ...prev, reshoot_all: fresh.reshoot_all, directory_image: fresh.directory_image, cover_image: fresh.cover_image, package: fresh.package, shot_gallery: fresh.shot_gallery }));
          if ((fresh.reshoot_all || {}).status === "done") { toast.success("Full re-shoot complete — their whole profile now matches."); onChange(); }
        }
      } catch {}
    }, 8000);
    return () => clearInterval(t);
  }, [reshootRunning, m.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const reshootAll = async () => {
    if (!window.confirm("Re-shoot ALL of their imagery to match the current focus?\n\nThis re-makes the Cover Member portrait, Editorial cover, every article image and the advert in one go — several image generations from your budget. It takes a few minutes.")) return;
    try { await client.post(`/admin/members/${m.id}/reshoot-all`); setM({ ...m, reshoot_all: { status: "running", done: 0, total: 0 } }); toast.success("The studio is re-shooting their entire profile — watch it update over the next few minutes."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start the re-shoot."); }
  };
  const addOriginal = async (articleId, path) => {
    try { await client.post(`/admin/articles/${articleId}/gallery/add-originals`, { paths: [path] }); toast.success("Their original photo placed inside the article — check Preview."); }
    catch { toast.error("Could not add the photo."); }
  };
  const genBlog = async () => {
    setBusy("blog");
    try { await client.post(`/admin/members/${m.id}/generate-blog`, { topic: blogTopic }); toast.success("Business article + brand image drafted — see Review & publish above."); setBlogTopic(""); loadContent(); }
    catch { toast.error("Blog generation failed."); }
    setBusy("");
  };
  const genAdvert = async () => {
    setBusy("advert");
    try { const r = await client.post(`/admin/members/${m.id}/generate-advert`); setM({ ...m, package: { ...(m.package || {}), advert: r.data } }); toast.success("Advert written by AI. Publish or schedule its run in Review & publish."); onChange(); }
    catch { toast.error("Advert generation failed."); }
    setBusy("");
  };
  const genAdvertImage = async () => {
    setBusy("advert-img");
    try { const r = await runJob(`/admin/members/${m.id}/generate-advert-image`); setAd("image", r.data.image); toast.success("Advert image generated — a distinct campaign look."); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Advert image failed — upload photos first."); }
    setBusy("");
  };
  const uploadAdvertImage = async (files) => {
    if (!files?.[0]) return; setBusy("advert-img");
    const fd = new FormData(); fd.append("file", files[0]);
    try { const r = await client.post(`/admin/members/${m.id}/advert-image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setAd("image", r.data.image); toast.success("Advert image uploaded."); onChange(); }
    catch { toast.error("Upload failed."); }
    setBusy("");
  };
  const uploadVenuePhotos = async (files) => {
    if (!files?.length) return; setBusy("venue-up");
    const fd = new FormData(); Array.from(files).slice(0, 5).forEach((f) => fd.append("files", f));
    try { const r = await client.post(`/admin/members/${m.id}/venue-photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setM({ ...m, venue_photos: r.data.venue_photos }); toast.success("Venue photos uploaded."); onChange(); }
    catch { toast.error("Upload failed."); }
    setBusy("");
  };
  const genVenue = async () => {
    setBusy("venue-gen");
    try { const r = await client.post(`/admin/members/${m.id}/generate-venue`); setM({ ...m, venue_gallery: r.data.venue_gallery }); toast.success("Venue imagery generated."); onChange(); }
    catch { toast.error("Venue generation failed."); }
    setBusy("");
  };
  const genSocial = async () => {
    setBusy("social");
    try { await client.post(`/admin/members/${m.id}/generate-social`); toast.success("Instagram post drafted (uses the Cover Member shot) — find it in the Social tab."); }
    catch { toast.error("Social generation failed."); }
    setBusy("");
  };
  const genFiveQ = async (publish) => {
    setBusy("fiveq");
    try {
      const r = await client.post(`/admin/members/${m.id}/generate-5q`, { publish: !!publish });
      toast.success(publish ? `"5 Questions" written & published to Interviews.` : `"5 Questions" drafted from their answers — review it in Editorial.`);
      onChange();
    } catch { toast.error("5 Questions generation failed."); }
    setBusy("");
  };
  const [editCat, setEditCat] = useState("Business");
  const [pubDate, setPubDate] = useState("");
  const genSpotlight = async (publish) => {
    setBusy("spotlight");
    try {
      await runJob(`/admin/members/${m.id}/generate-spotlight`, { publish: !!publish, publish_at: pubDate || null });
      toast.success(publish ? "Spotlight written & published." : "Spotlight drafted — review it in Editorial.");
      onChange();
    } catch { toast.error("Spotlight generation failed."); }
    setBusy("");
  };
  const genEdit = async (publish) => {
    setBusy("edit");
    try {
      await runJob(`/admin/members/${m.id}/generate-edit`, { category: editCat, publish: !!publish, publish_at: pubDate || null });
      toast.success(publish ? `The Edit (${editCat}) written & published.` : `The Edit (${editCat}) drafted.`);
      onChange();
    } catch { toast.error("Edit article generation failed."); }
    setBusy("");
  };
  const createNotice = async () => {
    if (!notice.title.trim()) { toast.error("Add a notice title."); return; }
    setBusy("notice");
    try {
      const r = await client.post("/admin/notices", { ...notice, member_id: m.id, member_name: m.business_name, borough: m.borough, status: "published" });
      setLastNoticeId(r.data.id);
      toast.success("Notice sent to the Noticeboard. Generate a poster below if you like.");
      setNotice({ title: "", body: "", type: "offer" });
    } catch { toast.error("Could not create notice."); }
    setBusy("");
  };
  const genNoticePoster = async () => {
    if (!lastNoticeId) { toast.error("Send a notice first."); return; }
    setBusy("poster");
    try { await client.post(`/admin/notices/${lastNoticeId}/generate-poster`); toast.success("Poster generated — see it in the Noticeboard tab."); }
    catch { toast.error("Poster generation failed."); }
    setBusy("");
  };
  const deleteMember = async () => {
    if (!window.confirm(`Delete ${m.business_name}? This removes their profile, feature, notices and social. This cannot be undone.`)) return;
    setBusy("delete");
    try { await client.delete(`/admin/members/${m.id}`); toast.success("Member deleted."); onBack(); }
    catch { toast.error("Delete failed."); setBusy(""); }
  };
  const genEverything = async () => {
    const nPhotos = (m.photos || []).length;
    const nDocs = (m.ref_docs || []).length;
    if (nPhotos === 0) {
      const ok = window.confirm("No photos uploaded yet — the AI covers work best from the owner's real photos. Generate anyway (covers may be less accurate)?");
      if (!ok) return;
    }
    setBusy("all");
    const fails = [];
    const step = async (label, fn) => { setGenStep(label); try { await fn(); } catch { fails.push(label); } };
    await step(`Reading ${nDocs} document(s) + ${nPhotos} photo(s), writing the feature & advert copy…`, async () => {
      const r = await client.post(`/admin/members/${m.id}/generate-package`); setM((p) => ({ ...p, package: r.data }));
    });
    if (nPhotos > 0) {
      await step("Designing the advert image from their photos…", async () => {
        const r = await runJob(`/admin/members/${m.id}/generate-advert-image`); setM((p) => ({ ...p, package: { ...(p.package || {}), advert: { ...((p.package || {}).advert || {}), image: r.data.image } } }));
      });
    }
    await step("Shooting the Cover Member portrait (S/ME + their logo)…", async () => {
      const r = await shootCover(m.id, { style: cmCoverStyle, preset: "directory", target: "directory_image", include_sme_logo: true }); setM((p) => ({ ...p, directory_image: r.data.image }));
    });
    await step("Shooting the Editorial cover…", async () => {
      const r = await shootCover(m.id, { style: edCoverStyle, preset: "editorial", target: "cover_image" }); setM((p) => ({ ...p, cover_image: r.data.image }));
    });
    await step("Writing the Spotlight article…", async () => {
      await runJob(`/admin/members/${m.id}/generate-spotlight`, {});
    });
    await step("Writing The Edit deep-dive…", async () => {
      await runJob(`/admin/members/${m.id}/generate-edit`, {});
    });
    await step("Composing the Interview (5 Questions)…", async () => {
      await client.post(`/admin/members/${m.id}/generate-5q`, {});
    });
    await step("Writing the Instagram + TikTok post…", async () => {
      await client.post(`/admin/members/${m.id}/generate-social`);
    });
    await step("Pinning a Noticeboard post…", async () => {
      const title = (m.offers || "").trim() || `Latest from ${m.business_name}`;
      const r = await client.post("/admin/notices", { title, body: m.bio || "", type: "offer", member_id: m.id, member_name: m.business_name, borough: m.borough, status: "published" });
      setLastNoticeId(r.data.id);
    });
    setGenStep("");
    if (fails.length === 0) toast.success("Full feature built from their documents & photos — covers, feature, Spotlight, The Edit, Interview, advert & social are ready. Review, then Publish.");
    else toast.error(`Done, but these need a retry: ${fails.join(", ")}`);
    onChange();
    setBusy("");
  };

  const superBuild = async () => {
    const url = (buildUrl || m.website || "").trim();
    if (!url) { toast.error("Paste their website in the build bar first — Super Build starts by reading it."); return; }
    if (!window.confirm(`Super Build for ${m.business_name}: reads ${url}, fills the whole profile & bios, imports their imagery, shoots the covers and generates the complete editorial package (feature, Spotlight, The Edit, Interview, advert & social). This uses a full round of AI credits. Go ahead?`)) return;
    setBusy("all");
    const fails = [];
    const step = async (label, fn) => { setGenStep(label); try { return await fn(); } catch { fails.push(label); return null; } };
    let latest = m;
    await step("1/8 · Reading their website — profile, bios, their imagery + fresh editorial cover…", async () => {
      const r = await client.post(`/admin/members/${m.id}/magic-build`, { url, mode: "media" });
      latest = r.data.member; setM(r.data.member);
    });
    await step("2/8 · Writing the feature & advert copy…", async () => {
      const r = await client.post(`/admin/members/${m.id}/generate-package`); setM((p) => ({ ...p, package: r.data }));
    });
    if ((latest.photos || []).length > 0) {
      await step("3/8 · Designing the advert image from their imagery…", async () => {
        const r = await runJob(`/admin/members/${m.id}/generate-advert-image`); setM((p) => ({ ...p, package: { ...(p.package || {}), advert: { ...((p.package || {}).advert || {}), image: r.data.image } } }));
      });
      await step("4/8 · Shooting the Cover Member portrait (S/ME + their logo)…", async () => {
        const r = await shootCover(m.id, { style: cmCoverStyle, preset: "directory", target: "directory_image", include_sme_logo: true }); setM((p) => ({ ...p, directory_image: r.data.image }));
      });
    }
    await step("5/8 · Writing the Spotlight article…", () => runJob(`/admin/members/${m.id}/generate-spotlight`, {}));
    await step("6/8 · Writing The Edit deep-dive…", () => runJob(`/admin/members/${m.id}/generate-edit`, {}));
    await step("7/8 · Composing the Interview (5 Questions)…", () => client.post(`/admin/members/${m.id}/generate-5q`, {}));
    await step("8/8 · Writing the social posts + pinning a Noticeboard offer…", async () => {
      await client.post(`/admin/members/${m.id}/generate-social`);
      const title = (latest.offers || m.offers || "").trim() || `Latest from ${latest.business_name || m.business_name}`;
      const r = await client.post("/admin/notices", { title, body: latest.bio || m.bio || "", type: "offer", member_id: m.id, member_name: latest.business_name || m.business_name, borough: latest.borough || m.borough, status: "published" });
      setLastNoticeId(r.data.id);
    });
    setGenStep("");
    if (fails.length === 0) toast.success("Super Build complete — the whole member is ready to review: profile, imagery, covers, feature, Spotlight, The Edit, Interview, advert & social. Review, then Publish.");
    else toast.error(`Done, but these need a retry: ${fails.join(", ")}`);
    onChange();
    setBusy("");
  };
  const feat = (m.package || {}).feature || {};
  const ad = (m.package || {}).advert || {};

  const DRIP = { spotlight: { editions: 1, when: "next edition" }, edit: { editions: 2, when: "in 2 editions" }, five_questions: { editions: 3, when: "in 3 editions" } };
  const [dripDate, setDripDate] = useState({});
  const defaultDrip = (type) => (m.year_plan || {})[type] || editionStartIso(cycleIndexFor() + (DRIP[type]?.editions || 1));
  const dripValue = (p) => dripDate[p.type] ?? (p.publish_at ? String(p.publish_at).slice(0, 10) : defaultDrip(p.type));

  // shared image controls for any member article — labelled, Share-Kit style
  const actionBtn = "overline flex items-center gap-2 border border-ink/30 px-3.5 py-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-50";
  const ImgTools = ({ p, showOptions }) => (
    <>
      <button onClick={() => regenArticleImage(p.id, p.type)} disabled={!!imgBusy} className={actionBtn} title="New AI photo (keeps the copy)" data-testid={`pub-newphoto-${p.type || p.id}`}>
        <RefreshCw size={13} className={imgBusy === p.type ? "animate-spin" : ""} /> New photo
      </button>
      {showOptions && (
        <button onClick={() => getImageOptions(p.id, p.type)} disabled={!!optBusy} className={actionBtn} title="Generate 3 photo options" data-testid={`pub-options-btn-${p.type}`}>
          {optBusy === p.type ? <RefreshCw size={13} className="animate-spin" /> : <LayoutGrid size={13} />} 3 options
        </button>
      )}
      <button onClick={() => buildSpread(p.id, p.type)} disabled={!!imgBusy} className={actionBtn} title="Shoot a 3-image spread for INSIDE this article (optional — pick the pieces that deserve it)" data-testid={`pub-spread-${p.type}`}>
        <Images size={13} /> Image spread
      </button>
      <button onClick={() => setPhotoPickFor(photoPickFor === p.id ? null : p.id)} className={`${actionBtn} ${photoPickFor === p.id ? "bg-ink text-cream" : ""}`} title="Place their ORIGINAL photos inside this article — no AI" data-testid={`pub-originals-${p.type}`}>
        <ImageIcon size={13} /> Their photos
      </button>
      <label className={`${actionBtn} cursor-pointer`} title="Upload your own photo (keeps the copy)" data-testid={`pub-uploadphoto-${p.type || p.id}`}>
        <Upload size={13} /> Upload
        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadArticleImage(p.id, p.type, e.target.files)} disabled={!!imgBusy} />
      </label>
      {p.slug && <button onClick={() => setPreviewSlug(p.slug)} className={actionBtn} title="Preview right here — no page jump" data-testid={`pub-preview-${p.type || p.id}`}><Eye size={13} /> Preview</button>}
    </>
  );

  const EditBlock = ({ id, testid }) => (
    editId === id ? (
      <div className="mt-4" data-testid={testid || `pub-edit-${id}`}>
        <div className="border border-ink/20 bg-secondary/40 p-4 space-y-3">
          <p className="overline text-ink/50">Edit the copy — nothing else changes</p>
          <Field label="Title / headline" v={editForm.title} on={(v) => setEditForm((f) => ({ ...f, title: v }))} testid={`edit-title-${id}`} />
          <Area label="Standfirst (dek)" v={editForm.dek} on={(v) => setEditForm((f) => ({ ...f, dek: v }))} rows={2} testid={`edit-dek-${id}`} />
          <Area label="Body" v={editForm.body} on={(v) => setEditForm((f) => ({ ...f, body: v }))} rows={12} testid={`edit-body-${id}`} />
          <div className="flex gap-2">
            <button onClick={saveEditPiece} disabled={editBusy} className="bg-ink text-cream px-4 py-2 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid={`edit-save-${id}`}>
              {editBusy ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save copy
            </button>
            <button onClick={() => setEditId(null)} className="border border-ink/30 px-4 py-2 overline hover:bg-ink hover:text-cream transition-colors" data-testid={`edit-cancel-${id}`}>Cancel</button>
          </div>
        </div>
      </div>
    ) : null
  );

  const PieceRow = ({ p, drip }) => (
    <div className="border border-ink/15 bg-white p-5 sm:p-6" data-testid={`pub-piece-${p.type}`}>
      <div className="flex flex-col sm:flex-row gap-5">
        <div className="w-24 sm:w-28 shrink-0">
          <div className="aspect-[3/4] bg-secondary overflow-hidden border border-ink/10 flex items-center justify-center">
            {p.cover_image ? <img src={imgUrl({ cover_image: p.cover_image })} alt="" className="w-full h-full object-cover object-top" /> : <ImageIcon size={20} className="text-ink/25" />}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="overline text-gloss-gold">{p.label}</p>
              {p.exists
                ? <h3 className="font-serif text-xl sm:text-2xl tracking-tight leading-tight mt-1">{p.title || "Untitled"}</h3>
                : <h3 className="font-serif text-xl sm:text-2xl tracking-tight leading-tight mt-1 text-ink/35 italic">Not created yet</h3>}
            </div>
            {p.exists ? <Badge status={p.status} /> : <span className="overline text-ink/30 border border-ink/15 px-2 py-1">empty</span>}
          </div>
          <div className="flex flex-wrap gap-2.5 mt-5">
            {p.exists && p.id && <ImgTools p={p} showOptions={["member_feature", "spotlight"].includes(p.type)} />}
            {p.exists && p.id && <button onClick={() => openEditPiece(p.id)} className={actionBtn} title="Edit the copy (title, standfirst, body)" data-testid={`pub-edit-btn-${p.type}`}><Pencil size={13} /> Edit text</button>}
            {p.exists && p.id && <button onClick={() => deleteArticle(p.id, p.label)} className="overline flex items-center gap-2 border border-ink/25 text-ink/50 px-3.5 py-2 hover:border-red-500 hover:text-red-500 transition-colors" title="Delete this piece" data-testid={`pub-delete-${p.type}`}><Trash2 size={13} /> Delete</button>}
            {p.exists
              ? <button onClick={() => setArticleStatus(p.id, p.status === "published" ? "draft" : "published")} className={`overline px-5 py-2 transition-colors whitespace-nowrap sm:ml-auto ${p.status === "published" ? "border border-ink hover:bg-ink hover:text-cream" : "bg-ink text-cream hover:bg-gloss-gold hover:text-ink"}`} data-testid={`pub-toggle-${p.type}`}>
                  {p.status === "published" ? "Unpublish" : "Publish now"}
                </button>
              : p.type === "member_feature"
                ? (m.package?.feature?.title
                    ? <button onClick={createFeaturePiece} className="overline bg-gloss-gold text-ink px-5 py-2 hover:bg-ink hover:text-cream transition-colors whitespace-nowrap sm:ml-auto" data-testid={`pub-make-${p.type}`}>Create from package</button>
                    : <button onClick={() => toast.info("Generate the Editorial Feature copy first — in The Pieces section of this page.")} className="overline border border-ink/40 text-ink/60 px-5 py-2 hover:border-ink hover:text-ink transition-colors whitespace-nowrap sm:ml-auto" data-testid={`pub-make-${p.type}`}>Needs feature copy</button>)
                : <button onClick={() => { if (p.type === "spotlight") genSpotlight(false); else if (p.type === "edit") genEdit(false); else if (p.type === "five_questions") genFiveQ(false); }} disabled={!!busy}
                    className="overline bg-ink text-cream px-5 py-2 hover:bg-gloss-gold hover:text-ink transition-colors whitespace-nowrap sm:ml-auto disabled:opacity-60 flex items-center gap-2" data-testid={`pub-make-${p.type}`}>
                    {busy === (p.type === "five_questions" ? "fiveq" : p.type) ? <RefreshCw size={13} className="animate-spin" /> : null} Create now
                  </button>}
          </div>
          {photoPickFor === p.id && p.exists && (
            <div className="mt-4 border border-ink/15 bg-secondary/40 p-4" data-testid={`originals-picker-${p.type}`}>
              <p className="overline text-ink/50 mb-3">Tap a photo to place it inside the article — their original photography, untouched</p>
              {[...(m.photos || []), ...(m.venue_photos || [])].length > 0 ? (
                <div className="grid grid-cols-6 sm:grid-cols-10 gap-2">
                  {[...(m.photos || []), ...(m.venue_photos || [])].map((ph, i) => (
                    <button key={ph} onClick={() => addOriginal(p.id, ph)} className="relative aspect-square border border-ink/15 overflow-hidden hover:border-gloss-gold transition-colors" title="Place inside article" data-testid={`orig-pick-${p.type}-${i}`}>
                      <img src={imgUrl({ cover_image: ph })} className="w-full h-full object-cover" alt="" />
                    </button>
                  ))}
                </div>
              ) : <p className="font-sans text-xs text-ink/45">No photos on file yet — upload some in section 1.</p>}
            </div>
          )}
          {drip && p.exists && p.id && (
            <div className="flex flex-wrap items-center gap-3 mt-5 border-t border-ink/10 pt-4" data-testid={`pub-schedule-${p.type}`}>
              <span className="overline text-ink/50">{p.status === "scheduled" && p.publish_at ? `Drips on ${String(p.publish_at).slice(0, 10)}` : `Suggested drop: ${DRIP[p.type]?.when} (${defaultDrip(p.type)})`}</span>
              <input type="date" value={dripValue(p)} onChange={(e) => setDripDate((d) => ({ ...d, [p.type]: e.target.value }))} className="border border-ink/25 px-3 py-2 font-sans text-sm bg-cream" data-testid={`pub-date-${p.type}`} />
              <button onClick={() => scheduleArticle(p.id, p.type, dripValue(p))} disabled={schedBusy === p.type} className="overline border border-ink/40 px-4 py-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-50" data-testid={`pub-schedule-btn-${p.type}`}>
                {schedBusy === p.type ? <RefreshCw size={13} className="animate-spin" /> : "Schedule drip"}
              </button>
              {p.status === "scheduled" && <button onClick={() => scheduleArticle(p.id, p.type, null)} className="overline text-ink/50 px-2 py-2 hover:text-red-500 transition-colors" data-testid={`pub-unschedule-${p.type}`}>Clear</button>}
            </div>
          )}
          {imgOptions[p.id] && (
            <div className="flex flex-wrap items-center gap-3 mt-4" data-testid={`pub-options-${p.type}`}>
              <span className="overline text-gloss-gold">Pick your favourite:</span>
              {imgOptions[p.id].map((opt, i) => (
                <button key={i} onClick={() => pickImageOption(p.id, opt)} className="w-20 h-24 overflow-hidden border-2 border-transparent hover:border-ink transition-colors" data-testid={`pub-option-${p.type}-${i}`}>
                  <img src={imgUrl({ cover_image: opt })} alt="" className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>
          )}
          <EditBlock id={p.id} testid={`pub-edit-${p.type}`} />
        </div>
      </div>
    </div>
  );
  const GroupHead = ({ title, note }) => (
    <div className="px-5 sm:px-6 pt-8 pb-4">
      <p className="font-grotesk text-lg font-medium tracking-tight border-b border-ink/15 pb-3">{title}</p>
      {note && <p className="font-sans text-sm text-ink/55 mt-2">{note}</p>}
    </div>
  );

  const StyleRefBar = () => (
    <div className="mt-4 border-t border-ink/10 pt-3 flex items-center gap-3 flex-wrap" data-testid="style-ref-bar">
      {styleRef ? (
        <>
          <img src={imgUrl({ cover_image: styleRef.image })} alt="" className="w-10 h-14 object-cover border border-gloss-gold shrink-0" />
          <p className="font-sans text-xs text-ink/65 min-w-0">Styling the next shoot like <b>{styleRef.business_name || "this shot"}</b> — look &amp; mood only, never the person.</p>
          <button onClick={() => setStyleRef(null)} className="overline text-[10px] border border-ink/30 px-2.5 py-1.5 hover:border-ink transition-colors" data-testid="style-ref-clear">Clear</button>
        </>
      ) : (
        <button onClick={openStylePicker} className="overline text-[10px] border border-ink/30 px-3 py-2 flex items-center gap-1.5 hover:bg-ink hover:text-cream transition-colors" data-testid="style-ref-open">
          <Star size={12} /> Style it like an existing studio shot…
        </button>
      )}
    </div>
  );

  const latestFilm = coverFilms.find((v) => v.status === "done" && v.video_path);
  const coverStarControls = (
    <div className="border border-gloss-gold/60 bg-gloss-gold/10 p-4" data-testid="cover-star-bar">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="overline text-ink flex items-center gap-2"><Star size={13} /> Cover Star{readiness?.is_cover_star ? " · THIS EDITION'S STAR" : ""}</p>
          <p className="font-sans text-xs text-ink/60 mt-1">This Spotlight can go on the FRONT of the current edition — it becomes the Cover Star Feature at the top of the magazine.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          {readiness?.is_cover_star ? (
            <button onClick={attachLatestFilm} disabled={!!busy}
              className="bg-gloss-gold text-ink px-4 py-2.5 overline text-[10px] flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid="attach-film-btn">
              {busy === "attachfilm" ? <RefreshCw size={13} className="animate-spin" /> : <Star size={13} />} Attach latest film / refresh cover
            </button>
          ) : (
            <button onClick={setCoverStar} disabled={!!busy}
              className="bg-gloss-gold text-ink px-4 py-2.5 overline text-[10px] flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid="make-cover-star-btn">
              {busy === "coverstar" ? <RefreshCw size={13} className="animate-spin" /> : <Star size={13} />} Make Cover Star
            </button>
          )}
          <button onClick={animateCoverStar} disabled={!!busy || !m.directory_image}
            className="border border-ink/40 px-4 py-2.5 overline text-[10px] flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid="animate-cover-star-btn">
            {busy === "animatestar" ? <RefreshCw size={13} className="animate-spin" /> : <Film size={13} />} Animate Cover Star
          </button>
        </div>
      </div>
      {coverFilms.length > 0 && (
        <div className="mt-4 pt-3 border-t border-ink/10" data-testid="cover-films-panel">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="overline text-[10px] text-ink/60 flex items-center gap-2"><Film size={12} /> Their cover films</p>
            <button onClick={loadCoverFilms} className="overline text-[9px] text-ink/50 hover:text-ink flex items-center gap-1" data-testid="cover-films-refresh"><RefreshCw size={10} /> Refresh</button>
          </div>
          {latestFilm && (
            <video controls playsInline src={`${API}/files/${latestFilm.video_path}`}
              className="w-full max-w-[240px] aspect-[2/3] object-cover border border-ink/20 mb-3 bg-ink" data-testid="cover-film-player" />
          )}
          <div className="space-y-1.5">
            {coverFilms.slice(0, 5).map((v, i) => (
              <div key={v.id} className="flex items-center gap-3 font-mono text-[11px]" data-testid={`cover-film-${i}`}>
                <span className={`px-2 py-0.5 uppercase tracking-wider text-[9px] ${v.status === "done" ? "bg-green-100 text-green-800" : v.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{v.status}</span>
                <span className="text-ink/50">{new Date(v.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                {v.status === "done" && v.video_path && (
                  <a href={`${API}/files/${v.video_path}`} target="_blank" rel="noreferrer" className="underline text-ink/70 hover:text-ink" data-testid={`cover-film-watch-${i}`}>Watch</a>
                )}
                {v.status === "failed" && v.error && <span className="text-red-600 truncate">{String(v.error).slice(0, 90)}</span>}
                <button onClick={async () => { if (!window.confirm("Delete this cover film? If it's attached to the cover it comes off the site too.")) return; try { await client.delete(`/admin/videos/${v.id}`); toast.success("Film deleted."); loadCoverFilms(); onChange(); } catch { toast.error("Couldn't delete."); } }}
                  className="ml-auto text-ink/40 hover:text-red-600 transition-colors" title="Delete this film" data-testid={`cover-film-delete-${i}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
          <p className="font-sans text-xs text-ink/50 mt-2">A DONE film attaches to the cover automatically. If it isn't on the homepage, press “Attach latest film” above.</p>
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="member-hub">
      {/* full-page hub header */}
      <div className="sticky top-0 z-30 bg-ink text-cream grain">
        <div className="relative z-10 px-6 sm:px-10 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button onClick={onBack} className="overline flex items-center gap-2 border border-cream/30 px-3 py-2 hover:bg-cream hover:text-ink transition-colors shrink-0" data-testid="hub-back">
              <ArrowLeft size={14} /> All members
            </button>
            <div className="min-w-0">
              <p className="overline text-gloss-gold">Member Studio</p>
              <p className="font-serif text-2xl sm:text-3xl truncate">{m.business_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge status={m.status} />
            {m.user_id ? (
              <button onClick={() => setShowForm(true)} className="border border-gloss-gold/70 text-gloss-gold px-3 py-2 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors" data-testid="hub-preview-form" title="See exactly what this member submitted via the portal form — every field, verbatim">
                <FileText size={14} /> Their form
              </button>
            ) : (
              <span className="hidden md:flex items-center gap-2 px-3 py-2 border border-cream/20 text-cream/60 font-mono text-[10px] uppercase tracking-widest" data-testid="hub-no-form-note">
                <FileText size={13} /> Created in the Studio · no member form
              </span>
            )}
            {m.status === "published" && <a href={`/member/${m.id}`} target="_blank" rel="noreferrer" className="border border-cream/30 px-4 py-2 overline hidden sm:flex items-center gap-2 hover:bg-cream hover:text-ink transition-colors"><Eye size={14} /> View live</a>}
            <button onClick={deleteMember} disabled={busy} className="border border-red-400/50 text-red-300 px-3 py-2 overline flex items-center gap-2 hover:bg-red-500 hover:text-cream transition-colors disabled:opacity-60" data-testid="delete-member">
              {busy === "delete" ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />} <span className="hidden sm:inline">Delete</span>
            </button>
            {m.status === "published" ? (
              <button onClick={() => setVisibility(false)} disabled={busy} className="border border-gloss-gold text-gloss-gold px-4 py-2 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="hide-member">
                {busy === "visibility" ? <RefreshCw size={14} className="animate-spin" /> : <Eye size={14} />} <span className="hidden sm:inline">Take off the magazine</span><span className="sm:hidden">Hide</span>
              </button>
            ) : m.status === "hidden" ? (
              <button onClick={() => setVisibility(true)} disabled={busy} className="bg-gloss-gold text-ink px-4 py-2 overline flex items-center gap-2 hover:bg-cream transition-colors disabled:opacity-60" data-testid="unhide-member">
                {busy === "visibility" ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} <span className="hidden sm:inline">Put back on the magazine</span><span className="sm:hidden">Show</span>
              </button>
            ) : (
              <button onClick={publish} disabled={busy} className="bg-cream text-ink px-4 py-2 overline flex items-center gap-2 hover:bg-gloss-gold transition-colors disabled:opacity-60" data-testid="publish-member">
                {busy === "publish" ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} Publish
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-10 pt-8 pb-24">
        <div className={`mb-6 flex items-center gap-3 border px-5 py-4 ${m.status === "published" ? "border-green-600/40 bg-green-600/5" : "border-gloss-gold bg-gloss-gold/15"}`} data-testid="status-banner">
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${m.status === "published" ? "bg-green-600" : "bg-gloss-gold"}`} />
          {m.status === "published" ? (
            <p className="font-sans text-sm text-ink"><strong>Published — live.</strong> This member is visible on the public Directory and Cover Members pages.</p>
          ) : m.status === "hidden" ? (
            <p className="font-sans text-sm text-ink"><strong>Off the magazine — everything kept.</strong> Profile, articles, advert and posters are hidden from readers, but nothing has changed in the Studio. Hit <strong>Put back on the magazine</strong> (top right) to restore it all exactly as it was.</p>
          ) : (
            <p className="font-sans text-sm text-ink"><strong>In Review — not public yet.</strong> Readers can&apos;t see this member. Hit <strong>Publish</strong> (top right) to put them on the Directory and Cover Members pages.</p>
          )}
        </div>

        <div className="mb-8 border border-ink/15 bg-white p-4 flex items-start gap-3" data-testid="studio-rebuild-note">
          <span className="w-2 h-2 rounded-full bg-gloss-gold mt-2 shrink-0" />
          <div>
            <p className="font-serif text-lg text-ink">The Studio is being rebuilt, section by section.</p>
            <p className="font-sans text-sm text-ink/60">All nine panels are live: <b>Master · Cover Member · The Edit · Spotlight · 5 Questions · Noticeboard · Members&apos; Desk · Advert · Social</b>. Automation is switched off everywhere.</p>
          </div>
        </div>

        {/* Anchor nav: horizontal tab strip across the top */}
        <StudioNav readiness={readiness} />

        {/* ===== Readiness checklist ===== */}
        {readiness && (
          <div className="mb-8 border border-ink/15 bg-white" data-testid="readiness-checklist">
            <div className="p-4 sm:p-5 border-b border-ink/10 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="overline text-ink/60">Readiness checklist{readiness.is_cover_star ? " · COVER STAR" : ""}</p>
                <p className="font-serif text-xl text-ink mt-0.5">{readiness.done_count} of {readiness.total} complete {readiness.ready ? "· ready to publish" : `· ${readiness.missing_required.length} required item${readiness.missing_required.length === 1 ? "" : "s"} left`}</p>
              </div>
              <div className="min-w-[180px] flex items-center gap-2" data-testid="readiness-bar-wrap">
                <div className="flex-1 h-2 bg-ink/10 relative overflow-hidden">
                  <div className={`absolute inset-y-0 left-0 transition-all ${readiness.ready ? "bg-green-600" : "bg-gloss-gold"}`}
                    style={{ width: `${(readiness.done_count / Math.max(1, readiness.total)) * 100}%` }} data-testid="readiness-bar" />
                </div>
                <span className="font-mono text-[10px] text-ink/50 tabular-nums">{Math.round((readiness.done_count / Math.max(1, readiness.total)) * 100)}%</span>
              </div>
            </div>
            <ul className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2" data-testid="readiness-items">
              {readiness.items.map((it) => (
                <li key={it.key} className="flex items-start gap-2.5 text-sm" data-testid={`readiness-item-${it.key}`}>
                  <span className={`w-4 h-4 shrink-0 mt-0.5 flex items-center justify-center text-[10px] ${it.done ? "bg-green-600 text-white" : it.required ? "border border-gloss-gold text-gloss-gold" : "border border-ink/25 text-ink/40"}`}>
                    {it.done ? "✓" : it.required ? "!" : "·"}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`font-sans ${it.done ? "text-ink/50 line-through" : "text-ink"}`}>{it.label}</span>
                    <span className="font-mono text-[9px] text-ink/35 ml-2">{it.section}{!it.required && !it.done && " · optional"}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <HubSection id="section-photoflow" label="Photo Flow" note="The simple path for a total re-edit: photos in → fresh re-shoot → pick keepers → use everywhere" single>
          <PhotoFlow member={m} setMember={setM} onChange={onChange} />
        </HubSection>

        <MasterSection member={m} setMember={setM} onChange={onChange} saveFields={saveFields} busy={busy} articles={articles} inUse={inUse} onArticlesChange={loadArticles} />

        <CoverMemberSection member={m} setMember={setM} onChange={onChange} />

        <HubSection id="section-business-images" label="Business Image Studio" note="On-brand product, premises & craft shots — no portraits" single>
          <BusinessImageStudio member={m} setMember={setM} articles={articles} onArticlesChange={loadArticles} />
        </HubSection>

        <EditSection member={m} onChange={onChange} article={articles.edit} onArticleChange={(a) => articleChanged("edit", a)} />

        <SpotlightSection member={m} article={articles.spotlight} onArticleChange={(a) => articleChanged("spotlight", a)} coverStar={coverStarControls} />

        <FiveQuestionsSection member={m} article={articles.five_questions} onArticleChange={(a) => articleChanged("five_questions", a)} />

        <NoticeboardSection member={m} onChange={onChange} />

        <MembersDeskSection member={m} article={articles.blog} onArticleChange={(a) => articleChanged("blog", a)} />

        <AdvertSection member={m} setMember={setM} onChange={onChange} />

        <SocialSection member={m} setMember={setM} onChange={onChange} />

        {/* Master "Save all" — flushes every field on the member to the backend in one go */}
        <div className="pt-8 mt-4 border-t border-ink/15 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4" data-testid="save-all-bar">
          <p className="font-sans text-sm text-ink/60 max-w-lg">
            Reached the end. Hit <b>Save all</b> to push every edit on this member — Master, Cover Member, contact details, socials — to the magazine in one go.
          </p>
          <button
            onClick={saveFields}
            disabled={busy === "save"}
            className="bg-ink text-cream px-8 py-4 overline text-sm flex items-center justify-center gap-3 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60 shrink-0"
            data-testid="save-all-btn"
          >
            {busy === "save" ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
            {busy === "save" ? "Saving…" : "Save all changes"}
          </button>
        </div>
      </div>
      {showForm && <MemberFormPreview member={m} onClose={() => setShowForm(false)} />}
    </div>
  );
}

/* ---------------- Articles ---------------- */
